import React, { useState, useEffect, useContext } from "react";
import { Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useForm } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { useLocation, updateLocation, createLocation } from "../util/db";
import { useTheme } from '@material-ui/core/styles';
import Geocode from "react-geocode";
import MapForLocationSelectSearchAutocomplete from "./MapForLocationSelectSearchAutocomplete";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { shootingTypes, prepwrapTypes, productionTypes } from "../util/locationTypes";
import CustomTypeDialogCreate from "./CustomTypeDialogCreate";
import CustomTypeDialogDelete from "./CustomTypeDialogDelete";
import { CategoryContext } from "../providers/CategoryProvider";
import { toast } from 'react-hot-toast';

const useStyles = makeStyles(() => ({
  content: {
    paddingBottom: 24
  },
  buttonBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    gap: 20
  }
}));

function EditLocationModal(props) {
  const { category } = useContext(CategoryContext);
  const classes = useStyles();
  const [pending, setPending] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const theme = useTheme();
  const ifSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { register, handleSubmit, watch, formState: { isDirty } } = useForm();
  const { data: locationData, status: locationStatus } = useLocation(props.id);
  const [mapData, setMapData] = useState(null);
  const [typesList, setTypesList] = useState([]);
  const [type, setType] = useState(
    props.locationType || 
    (category === 'shooting' ? shootingTypes[0].type :
    category === 'prepwrap' ? prepwrapTypes[0].type :
    productionTypes[0].type)
  );
  const [createCustomTypeOpen, setCreateCustomTypeOpen] = useState(false);
  const [deleteCustomTypeOpen, setDeleteCustomTypeOpen] = useState(false);

  const values = watch(); // 🔥 live watch all form values

  Geocode.setApiKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);

  useEffect(() => {
    setTypesList(
      category === 'shooting' && props.groupCustomTypes ? [...shootingTypes, ...props.groupCustomTypes] :
      category === 'shooting' ? [...shootingTypes] :
      category === 'prepwrap' && props.groupCustomTypes ? [...prepwrapTypes, ...props.groupCustomTypes] :
      category === 'prepwrap' ? [...prepwrapTypes] :
      category === 'production' && props.groupCustomTypes ? [...productionTypes, ...props.groupCustomTypes] :
      [...productionTypes]
    );
  }, [category, props.groupCustomTypes]);

  useEffect(() => {
    if (props.id) {
      setMapData(locationData?.mapData);
    } else {
      navigator.geolocation.getCurrentPosition(
        (res) => {
          const lat = res.coords.latitude;
          const lng = res.coords.longitude;
          Geocode.fromLatLng(lat, lng).then(response => {
            const address = response.results[2].formatted_address;
            setMapData({ address, coords: { lat, lng } });
          }).catch(console.error);
        },
        (err) => {
          console.error(err);
          setMapData({ address: 'Worcester, MA, USA', coords: { lat: 42.2625932, lng: -71.8022934 } });
        }
      );
    }
  }, [locationData?.mapData, props.id]);

  if (props.id && locationStatus !== "success") {
    return null;
  }

  const onSubmit = (data) => {
    setPending(true);
    const typeOrder = typesList.find((list) => list.type === type);
    const updatedData = {
      name: data?.name?.toUpperCase(),
      type: type,
      typeOrder: props.new ? typeOrder?.orderNumber : props.locationTypeOrder,
      setName: ((/\bset\b/i.test(type))) ? data?.setName?.toUpperCase() : null,
      mapData: mapData,
      category: category,
      groupId: props.groupId
    };

    const query = props.id
      ? updateLocation(props.id, updatedData)
      : createLocation({ productionId: props.productionId, productionName: props.productionName, ...updatedData });

    query.then((response) => {
      if (response.success) {
        toast.success(response.message);
        props.onDone();
      } else {
        toast.error(response.message);
      }
    }).catch((error) => {
      toast.error(error.message);
      setPending(false);
    });
  };
  
  const ready = !!locationData || props.new; // locationData exists OR it's a new location

  // 🔥 Save Button Logic
  const nameChanged = values?.name !== locationData?.name;
  const mapChanged = JSON.stringify(mapData) !== JSON.stringify(locationData?.mapData);
  const typeChanged = type !== locationData?.type;
  const setNameChanged = ready && values?.setName !== locationData?.setName;

  const hasOtherEmptyFields = Object.entries(values || {})
    .filter(([key]) => key !== 'name') // ignore name
    .some(([, value]) => value === '' || value === undefined);

  const canSave = props.new
    ? !hasOtherEmptyFields
    : ready && (nameChanged || setNameChanged || mapChanged || typeChanged) && !hasOtherEmptyFields;


  return (
    <Dialog
      disableEnforceFocus
      style={{ pointerEvents: 'none' }}
      PaperProps={{ style: { pointerEvents: 'auto' } }}
      open={true}
      onClose={props.onDone}
      fullScreen={ifSmall}
      fullWidth
      maxWidth='xl'
    >
      <DialogTitle className="editHeader">
        {props.id ? 'Update' : 'Create'} Location
      </DialogTitle>

      <DialogContent className={classes.content}>
        {formAlert && (
          <Box mb={4}>
            <Alert severity={formAlert.type}>{formAlert.message}</Alert>
          </Box>
        )}

        {(locationStatus === "loading") ? (
          <Box py={5} px={3} align="center">
            <CircularProgress size={32} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* TYPE SELECT */}
              <Grid item xs={ifSmall ? 12 : ((/\bset\b/i.test(type)) ? 3 : 6)}>
                <TextField
                  variant="outlined"
                  id="locationTypeSelect"
                  label="TYPE"
                  name="type"
                  error={false}
                  select
                  fullWidth
                  value={type}
                  onChange={(e) => setType(e.target?.value || type)}
                >
                  {typesList?.sort((a, b) => a?.orderNumber - b?.orderNumber)
                    .map((theType) => (
                      <MenuItem key={theType?.orderNumber} value={theType?.type}>
                        {theType?.type} {props.groupCustomTypes?.some(ct => ct.type === theType?.type) && <span style={{ fontSize: '.7em', paddingLeft: 4 }}>(custom)</span>}
                      </MenuItem>
                    ))}
                  <div className={classes.buttonBar}>
                    {props.groupCustomTypes?.length > 0 && (
                      <Button variant="contained" color="secondary" onClick={() => setDeleteCustomTypeOpen(true)}>
                        DELETE CUSTOM
                      </Button>
                    )}
                    <Button variant="contained" color="primary" onClick={() => setCreateCustomTypeOpen(true)}>
                      + ADD CUSTOM
                    </Button>
                  </div>
                </TextField>
              </Grid>
              {/* SET NAME FIELD */}
              {(/\bset\b/i.test(type)) &&  (
                <Grid item xs={ifSmall ? 12 : ((/\bset\b/i.test(type)) ? 3 : 6)}>
                  <TextField
                    autoComplete="off"
                    variant="outlined"
                    type="text"
                    label="SET NAME"
                    name="setName"
                    defaultValue={locationData?.setName}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    inputRef={register({ required: "Please enter a set name" })}
                    fullWidth
                  />
                </Grid>
              )}

              {/* DESCRIPTION FIELD */}
              <Grid item xs={ifSmall ? 12 : 6}>
                <TextField
                  autoComplete="off"
                  variant="outlined"
                  type="text"
                  label="NAME / DESCRIPTION OF LOCATION (Optional)"
                  name="name"
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  defaultValue={locationData?.name}
                  inputRef={register()}
                  fullWidth
                />
              </Grid>

              {/* MAP */}
              <Grid item xs={12}>
                <MapForLocationSelectSearchAutocomplete
                  setMapData={setMapData}
                  mapData={mapData || locationData?.mapData}
                />
              </Grid>

              {/* FOOTER BUTTONS */}
              <Grid item xs={12} className="editFooter">
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={props.onDone}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleSubmit(onSubmit)}
                  variant="contained"
                  color="primary"
                  size="large"
                  type="button"
                  disabled={pending || !canSave}
                >
                  {!pending ? <span>Save</span> : <CircularProgress size={28} />}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}

        {/* Custom Type Dialogs */}
        <CustomTypeDialogCreate open={createCustomTypeOpen} setOpen={setCreateCustomTypeOpen} fullTypesList={typesList} customTypes={props.groupCustomTypes} groupId={props.groupId} listLength={typesList?.length} setType={setType} />
        <CustomTypeDialogDelete open={deleteCustomTypeOpen} setOpen={setDeleteCustomTypeOpen} groupId={props.groupId} customTypes={props.groupCustomTypes} />
      </DialogContent>
    </Dialog>
  );
}

export default EditLocationModal;
